import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Grid from "@material-ui/core/Grid";
import { schema } from "./schema";
import { useServiceFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../styles";
import { AttendanceLocalServiceCreateData } from "../../../../../../../services/api-tansquery/attendance-local/service/create";
import { AttendanceLocalService } from "../../../../../../../@types/attendance-local-service";
import { AttendanceLocalServiceResponse } from "../../../../../../../@types/attendance-local-service-response";
import { UseMutationResult } from "@tanstack/react-query";
import { AttendanceLocalServiceUpdateData } from "../../../../../../../services/api-tansquery/attendance-local/service/update";
import ServiceField from "./components/service-field";

type ServiceFormProps = {
    slug: string;
    service?: AttendanceLocalService;
    buttonTrigger?: React.ReactNode;
    mutation: UseMutationResult<
        AttendanceLocalServiceResponse,
        unknown,
        AttendanceLocalServiceCreateData | AttendanceLocalServiceUpdateData,
        unknown
    >
};

export default function ServiceForm({ slug, service, buttonTrigger, mutation }: ServiceFormProps) {
    const [open, setOpen] = useState(false);
    const { classes } = useServiceFormStyles();
    const { classes: defaultClasses } = useAttendanceLocalStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            servico_id: "",
            unidade: slug || "",
            agendamento: false,
            atendimento: false,
        }
    });

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            unidade: slug,
            servico_id: Number(data.servico_id),
            agendamento: data.agendamento,
            atendimento: data.atendimento,
        });
    }

    useEffect(() => {
        if (open && service) {
            form.reset({
                servico_id: service.servico.id.toString(),
                unidade: service.unidade,
                agendamento: service.agendamento,
                atendimento: service.atendimento,
            });
        } else {
            form.reset();
        }
    }, [open, service, form]);

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Serviço
                </Button>
            )}
            <Dialog open={open}>
                <Card
                    component="form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                >
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={<Typography variant="h5">Serviço</Typography>}
                    />
                    <CardContent
                        container
                        spacing={2}
                        component={Grid}
                    >
                        {!service && (
                            <Grid item xs={12}>
                                <Controller
                                    name="servico_id"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <ServiceField
                                            slug={slug}
                                            field={field}
                                            fieldState={fieldState}
                                            style={{ flex: 3 }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="atendimento"
                                control={form.control}
                                render={({ field }) => (
                                    <FormControl>
                                        <FormControlLabel
                                            label="Atendimento presencial disponível?"
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(e, checked) => {
                                                        field.onChange(checked);
                                                    }}
                                                />
                                            }
                                        />
                                        <FormHelperText>
                                            Marque se este serviço pode ser realizado presencialmente neste local.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="agendamento"
                                control={form.control}
                                render={({ field }) => (
                                    <FormControl>
                                        <FormControlLabel
                                            label="Atendimento online disponível?"
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(e, checked) => {
                                                        field.onChange(checked);
                                                    }}
                                                />
                                            }
                                        />
                                        <FormHelperText>
                                            Marque se este serviço está disponível digitalmente via plataforma online.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={defaultClasses.addButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
